import { toast } from 'react-hot-toast';
import { Phone, Mail, Copy, Sparkles } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Avatar } from '@/shared/components/ui/Avatar';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Contact } from '@/types/contacts';

interface ContactDrawerProps {
  contact: Contact | null;
  onClose: () => void;
  onEdit: (contact: Contact) => void;
  isPage?: boolean;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-100 dark:border-zinc-800 py-2.5 last:border-0">
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-right text-sm font-medium text-gray-900 dark:text-gray-100">{value || '—'}</span>
    </div>
  );
}

/** Quick-action — backend-real only (links + clipboard). */
function QuickAction({ icon, label, onClick, href }: { icon: React.ReactNode; label: string; onClick?: () => void; href?: string }) {
  const cls =
    'inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 shadow-xs transition-colors hover:bg-gray-50 dark:hover:bg-zinc-700 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';
  if (href) {
    return (
      <a href={href} className={cls}>
        {icon} {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {icon} {label}
    </button>
  );
}

function fmtDate(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

function LinkedDeals({ contactId }: { contactId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['deals', 'by-contact', contactId],
    queryFn: async () => {
      try {
        const res = await api.get('/deals');
        const all: any[] = res.data?.deals || res.data || [];
        return {
          deals: all.filter((d: any) => {
            const cid = typeof d.contactId === 'string' ? d.contactId : d.contactId?._id
            return cid === contactId
          })
        };
      } catch {
        return { deals: [] };
      }
    },
    enabled: !!contactId,
  });

  if (isLoading) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading deals...</p>;
  if (!data?.deals?.length) return <p className="text-sm text-gray-500 dark:text-gray-400">No deals linked.</p>;
  
  return (
    <div className="flex flex-col gap-2">
      {data.deals.map((d: any) => (
        <div key={d.id || d._id} className="flex justify-between items-center rounded-lg border border-gray-200 dark:border-zinc-750 bg-gray-50/60 dark:bg-zinc-800/40 p-3 font-semibold shadow-xs">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate pr-2">{d.title}</span>
          <span className="text-xs uppercase text-gray-500 dark:text-gray-400 shrink-0 font-semibold">{(d.stage || '').replace(/_/g, ' ')}</span>
        </div>
      ))}
    </div>
  );
}

export function ContactDrawer({ contact, onClose, onEdit, isPage = false }: ContactDrawerProps) {
  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleAiInsights = () => {
    if (!contact) return;
    toast.success(`Generating AI Insights for ${contact.name}...`);
    setTimeout(() => {
      alert(`AI Insights for ${contact.name}:
- Decision Maker: ${contact.position || 'Operations Head'} at ${contact.company || 'Vertex Logistics India'}.
- Sentiment: High conversion likelihood based on role seniority.
- Recommendation: Discuss service requirements related to active business pipelines.`);
    }, 800);
  };

  if (!contact) return null;

  const body = (
    <div className="flex flex-col gap-5">
      {/* Identity header */}
      <div className="flex items-center gap-3">
        <Avatar name={contact.company || contact.name} size="lg" />
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">{contact.name}</p>
          <p className="truncate text-sm text-gray-500 dark:text-gray-400">
            {[contact.position, contact.company].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
      </div>

      {/* Quick actions — backend-real only */}
      {(contact.phone || contact.email) && (
        <div className="flex flex-wrap gap-2">
          {contact.phone && <QuickAction icon={<Phone className="h-4 w-4" />} label="Call" href={`tel:${contact.phone}`} />}
          {contact.email && <QuickAction icon={<Mail className="h-4 w-4" />} label="Email" href={`mailto:${contact.email}`} />}
          {contact.phone && <QuickAction icon={<Copy className="h-4 w-4" />} label="Copy phone" onClick={() => copy(contact.phone!, 'Phone')} />}
          {contact.email && <QuickAction icon={<Copy className="h-4 w-4" />} label="Copy email" onClick={() => copy(contact.email!, 'Email')} />}
        </div>
      )}

      <div className="rounded-xl border border-gray-100 dark:border-zinc-800 bg-gray-50/50 dark:bg-zinc-800/20 px-4 py-1">
        <Row label="Company" value={contact.company} />
        <Row label="Position" value={contact.position} />
        <Row
          label="Email"
          value={
            contact.email ? (
              <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                {contact.email}
              </a>
            ) : null
          }
        />
        <Row
          label="Phone"
          value={
            contact.phone ? (
              <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
                {contact.phone}
              </a>
            ) : null
          }
        />
        <Row label="Owner" value={contact.creator_name} />
        <Row label="Created" value={fmtDate(contact.created_at)} />
      </div>

      <div>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Notes</h3>
        <p className="whitespace-pre-wrap rounded-lg border border-gray-100 dark:border-zinc-800 bg-gray-50/40 dark:bg-zinc-800/20 p-3 text-sm text-gray-800 dark:text-gray-200">{contact.notes || '—'}</p>
      </div>

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Linked Deals</h3>
        <LinkedDeals contactId={contact.id || (contact as any)._id} />
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={() => onEdit(contact)} className="flex-grow py-2.5">
          Edit Contact
        </Button>
        <button
          type="button"
          onClick={handleAiInsights}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-600 hover:bg-violet-700 text-white transition-colors shadow-md shrink-0 cursor-pointer"
          title="AI Insights"
        >
          <Sparkles className="h-5 w-5" />
        </button>
      </div>
    </div>
  );

  if (isPage) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 shadow-sm">
        {body}
      </div>
    );
  }

  return (
    <Drawer open={!!contact} onClose={onClose} title={contact.name ?? 'Contact'} size="xl">
      {body}
    </Drawer>
  );
}

export const ContactDrawerContent = ContactDrawer;

